import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
	AlertDialogCancel,
} from "@/components/primitives";
import { AssignedTopics } from "@/components/AssignedTopics";
import { EdgeFunctionsStatus } from "@/components/EdgeFunctionsStatus";
import { DailyCheckInAnalysisClean } from "@/components/DailyCheckInAnalysisClean";
import { TopPriorityAction } from "@/components/TopPriorityAction";
import { ReflectionProgressMeter } from "@/components/ReflectionProgressMeter";
import { buttonVariants } from "@/components/ui/button";
import { OnboardingTutorial } from "@/components/OnboardingTutorial";
import { formatRelativeDate } from "@/lib/utils";
import { Plus, Calendar, Lightbulb, BarChart3 } from "lucide-react";
import type { ReflectionGoal } from "@/lib/reflection-progress";

// Import types
import type { ChartViewMode } from "./HomePage/types";

// Import hooks
import { useDashboardData } from "./HomePage/hooks/useDashboardData";
import { useDraftManagement } from "./HomePage/hooks/useDraftManagement";
import { useAnalysisSync } from "./HomePage/hooks/useAnalysisSync";

// Import components
import { DraftReflectionCard } from "./HomePage/components/DraftReflectionCard";
import { MoodChart } from "./HomePage/components/MoodChart";
import { RecentCheckInItem } from "./HomePage/components/RecentCheckInItem";
import { SuggestedReflection as MultiSuggestedReflection } from "@/pages/DailyCheckIn/components/SuggestedReflection";
import type { AnalysisData as DCAnalysisData } from "@/pages/DailyCheckIn/types";

export function HomePage() {
	const { user, profile } = useAuth();
	const [chartViewMode, setChartViewMode] = useState<ChartViewMode>("entries");
	const [progressRefreshKey, setProgressRefreshKey] = useState(0);

	// Data fetching
	const {
		reflections,
		draftReflections,
		recentCheckIns,
		chartData,
		stats,
		loading,
		refetch,
	} = useDashboardData(chartViewMode);

	// Draft management
	const handleDraftDeleted = useCallback(
		(draftId: string) => {
			refetch();
		},
		[refetch],
	);

	const {
		deleteDialogOpen,
		deleting,
		handleDeleteDraft,
		confirmDeleteDraft,
		cancelDeleteDraft,
	} = useDraftManagement(handleDraftDeleted);

	// Analysis sync
	const { analysisRef, analysisData } = useAnalysisSync();

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
				<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
					<div className="flex items-center justify-center h-64">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
				<EdgeFunctionsStatus />

				{/* Onboarding Tutorial */}
				<OnboardingTutorial
					hasReflections={stats.totalReflections > 0}
					hasCheckIns={stats.totalCheckIns > 0}
				/>

				{/* Welcome Header */}
				<div className="mb-8">
					<div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
						<div>
							<h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-2">
								Welcome
							</h1>
							<p className="text-muted-foreground">
								Track your progress and manage your check ins and reflections
							</p>
						</div>
						<div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3 w-full lg:w-auto">
							<Link
								to={
									stats.totalCheckIns > 0
										? "/daily-checkin?skipIntro=true"
										: "/daily-checkin"
								}
								className="w-full sm:w-auto"
								data-tutorial="create-checkin"
							>
								<Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
									<Calendar className="h-4 w-4 mr-2" />
									Add Check in
								</Button>
							</Link>
							<Link
								to="/reflection/new"
								className="w-full sm:w-auto"
								data-tutorial="create-reflection"
							>
								<Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
									<Plus className="h-4 w-4 mr-2" />
									Create a Reflection
								</Button>
							</Link>
						</div>
					</div>
				</div>

				{/* Main Layout */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Main Content */}
					<div className="lg:col-span-2 space-y-8">
						{/* Draft Reflections */}
						{draftReflections.length > 0 && (
							<div>
								<h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-4">
									Continue Your Reflection
								</h2>
								<div className="grid grid-cols-1 gap-4">
									{draftReflections.map((draft) => (
										<DraftReflectionCard
											key={draft.id}
											draft={draft}
											onDelete={handleDeleteDraft}
										/>
									))}
								</div>
							</div>
						)}

						{/* Mood Chart */}
						<MoodChart
							data={chartData}
							viewMode={chartViewMode}
							onViewModeChange={setChartViewMode}
						/>

						{/* Daily Check-in Analysis */}
						<DailyCheckInAnalysisClean
							ref={analysisRef}
							activeReflections={reflections}
						/>

						{/* Suggested Reflections from Check-ins (up to 3) */}
						{(() => {
							const list =
								(analysisData as any)?.reflectionSuggestions &&
								(analysisData as any).reflectionSuggestions.length > 0
									? ((analysisData as any).reflectionSuggestions as Array<{
											title: string;
											description: string;
											reasoning?: string;
										}>)
									: analysisData?.reflectionSuggestion
										? [analysisData.reflectionSuggestion]
										: [];
							if (!list || list.length === 0) return null;
							const composed: DCAnalysisData = {
								mainTopics: [],
								negativeTopics: [],
								positiveTopics: [],
								reflectionSuggestion: list[0] || null,
								reflectionSuggestions: list,
							};
							const activeReflections = reflections.filter((r) => !r.archived);
							return (
								<MultiSuggestedReflection
									analysisData={composed}
									reflections={activeReflections}
								/>
							);
						})()}

						{/* Reflections List */}
						<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
							<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
										<Lightbulb className="h-5 w-5 text-white" />
									</div>
									<div>
										<CardTitle className="text-xl sm:text-2xl font-semibold">
											Your Reflections
										</CardTitle>
									</div>
								</div>
							</CardHeader>
							<CardContent className="pt-6">
								{reflections.length > 0 ? (
									<div className="space-y-4">
										{reflections.map((reflection) => (
											<div
												key={reflection.id}
												className="border-2 rounded-lg p-4 bg-white/50 dark:bg-slate-700/50 border-border/50 hover:border-primary/50 transition-all duration-200 hover:shadow-md"
											>
												<div className="space-y-3 sm:space-y-0">
													<div className="sm:flex sm:items-start sm:justify-between">
														<div className="flex-1 min-w-0">
															<h4 className="font-semibold text-foreground break-words pr-2 sm:pr-4">
																{reflection.title}
															</h4>
															<div className="flex items-center flex-wrap gap-2 mt-2">
																<span className="text-xs text-muted-foreground shrink-0">
																	{formatRelativeDate(reflection.created_at)}
																</span>
																{(Array.isArray(reflection.topic_tags)
																	? reflection.topic_tags.filter(
																			(tag) =>
																				!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
																					tag,
																				),
																		)
																	: []
																).map((tag) => (
																	<span
																		key={tag}
																		className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground shrink-0"
																	>
																		{tag}
																	</span>
																))}
															</div>
														</div>

														<div className="flex flex-col space-y-2 sm:ml-4 sm:flex-row sm:space-y-0 sm:space-x-2 sm:shrink-0">
															<Link
																to={`/reflection/${reflection.id}`}
																className="w-full sm:w-auto"
															>
																<Button
																	size="sm"
																	variant="outline"
																	className="w-full sm:w-auto text-xs sm:text-sm"
																>
																	<Lightbulb className="h-4 w-4 mr-2" />
																	<span className="sm:hidden">View</span>
																	<span className="hidden sm:inline">
																		View Reflection
																	</span>
																</Button>
															</Link>
															{reflection.hasAnalysis && (
																<Link
																	to={`/reflection/${reflection.id}?tab=analysis`}
																	className="w-full sm:w-auto"
																>
																	<Button
																		size="sm"
																		variant="outline"
																		className="w-full sm:w-auto text-xs sm:text-sm"
																	>
																		<BarChart3 className="h-4 w-4 mr-2" />
																		<span className="sm:hidden">Analysis</span>
																		<span className="hidden sm:inline">
																			View Analysis
																		</span>
																	</Button>
																</Link>
															)}
														</div>
													</div>
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-8">
										<div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 w-fit mx-auto mb-4">
											<Lightbulb className="h-8 w-8 text-white" />
										</div>
										<h4 className="text-lg font-semibold text-foreground mb-2">
											No reflections yet
										</h4>
										<p className="text-muted-foreground mb-4">
											Start your journey by creating your first reflection
										</p>
										<Link to="/reflection/new">
											<Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
												<Plus className="h-4 w-4 mr-2" />
												Create Your First Reflection
											</Button>
										</Link>
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Sidebar */}
					<div className="lg:col-span-1 space-y-6">
						{/* Reflection Progress Meter */}
						{user && profile?.reflection_goal && (
							<ReflectionProgressMeter
								userId={user.id}
								goal={profile.reflection_goal as ReflectionGoal}
								onRefresh={progressRefreshKey > 0}
								dayStreak={stats.dayStreak}
							/>
						)}

						{/* Top Priority Action */}
						<TopPriorityAction variant="homepage" />

						{/* Assigned Topics */}
						<AssignedTopics showStartButton={true} />

						{/* Recent Check-ins */}
						<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
							<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
										<Calendar className="h-5 w-5 text-white" />
									</div>
									<div>
										<CardTitle className="text-xl sm:text-2xl font-semibold">
											Recent Check ins
										</CardTitle>
										<CardDescription>
											Your latest daily check-ins
										</CardDescription>
									</div>
								</div>
							</CardHeader>
							<CardContent className="pt-6">
								{recentCheckIns.length > 0 ? (
									<>
										<div className="space-y-4">
											{recentCheckIns.map((checkIn) => (
												<RecentCheckInItem key={checkIn.id} checkIn={checkIn} />
											))}
										</div>
										<div className="mt-4 pt-4 border-t border-border">
											<Link to="/check-ins">
												<Button variant="outline" size="sm" className="w-full">
													View all Check ins
												</Button>
											</Link>
										</div>
									</>
								) : (
									<div className="text-center py-4">
										<p className="text-muted-foreground text-sm mb-4">
											No check-ins yet. Start tracking your daily progress.
										</p>
										<Link to="/daily-checkin">
											<Button
												size="sm"
												className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
											>
												Add your first check-in
											</Button>
										</Link>
									</div>
								)}
							</CardContent>
						</Card>
					</div>
				</div>

				{/* Delete Confirmation Dialog */}
				<AlertDialog open={deleteDialogOpen} onOpenChange={cancelDeleteDraft}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Delete Draft Reflection</AlertDialogTitle>
							<AlertDialogDescription>
								Are you sure you want to delete this draft reflection? This
								action cannot be undone.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel onClick={cancelDeleteDraft}>
								Cancel
							</AlertDialogCancel>
							<AlertDialogAction
								className={buttonVariants({ variant: "destructive" })}
								onClick={confirmDeleteDraft}
								disabled={deleting}
							>
								{deleting ? "Deleting..." : "Delete"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</div>
	);
}
